import React from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";

import { StepRentStatus } from "../../../types/rentTypes";

import {
  displayISODate,
  getDateRangeString,
  getTimeFromDateString,
} from "../../../libs/dateFormat";

import { HistoryItem } from "../../../services/historyServices";

import VerticalRule from "../../Rule/Vertical";
import HorizontalRule from "../../Rule/Horizontal";
import ProgressBar from "../../ProgressBar";
import { CancelCompleteBadge } from "../IncommodityItem";

import { ReportStatusRadioIcon } from "../../../pages/Manager/Report";
import {
  HistoryReservationCalendarIcon,
  HistoryReservationLocationIcon,
} from "../ReservationItem";

type Props = { index: number; item: HistoryItem; items: HistoryItem[] };

export default function RentItem({ index, item, items }: Props) {
  const navigate = useNavigate();

  const getRentStatus = (status: string) => {
    switch (status) {
      case "REQUESTED":
        return "REQUESTED";
      case "RENTED":
      case "OVERDUE":
        return "PROGRESSED";
      case "COMPLETED":
      case "LOST_DAMAGED":
      case "DONE":
        return "COMPLETED";
      case "CANCELED":
        return "CANCELED";
      default:
        return "DEFAULT";
    }
  };

  const getRentTypeIcon = (type: string) => {
    switch (type) {
      case "UMBRELLA":
        return <HistoryRentUmbrellaItemIcon />;
      case "BOOK":
        return <HistoryRentBookItemIcon />;
      case "TOOLS":
        return <HistoryRentToolItemIcon />;
      case "ITEM":
      default:
        return <HistoryRentItemIcon />;
    }
  };

  return (
    <React.Fragment key={index}>
      <div className="flex gap-2.5 relative">
        <div className="h-full w-[24px] flex justify-center absolute z-0">
          {items.length > 1 && (
            <VerticalRule
              className={clsx([
                "!h-full !bg-[#d9d9d9]",
                {
                  "!h-[calc(100%-24px)] ": index === 0,
                  "!h-[24px]": index === items.length - 1,
                },
              ])}
              wrapperClassName={clsx([
                {
                  "!items-end": index === 0,
                  "!items-start": index === items.length - 1,
                },
              ])}
            />
          )}
        </div>
        <div className="w-[24px] pt-[18px] z-10">
          <ReportStatusRadioIcon status={getRentStatus(item.status)} />
        </div>
        <div className="flex flex-col gap-1 pt-[18px]">
          <div className="text-base font-bold">
            {getTimeFromDateString(item.createdAt)}
          </div>
          <div className=" text-xs text-[#9f9f9f]">
            {item.createdAt.split(" ")[0]}
          </div>
          {item.status === "CANCELED" && <CancelCompleteBadge />}
        </div>
        <div
          className={clsx([
            "p-[15px] border border-brand-neutral-10 rounded-[10px] flex flex-col gap-2.5 flex-1 h-fit",
            {
              "bg-brand-neutral-05": item.status === "CANCELED",
            },
          ])}
          onClick={() => navigate(`/history/${item.type}/${item.serviceId}`)}
        >
          <div className="flex gap-2.5">
            {getRentTypeIcon(item.description)}
            <div className="flex flex-col gap-0.5">
              <div
                className={clsx([
                  {
                    " text-brand-neutral-50": item.status === "CANCELED",
                  },
                ])}
              >
                {item.lowerCategoryName}
              </div>
              <div className="text-xs text-brand-neutral-50">
                {item.midCategoryName}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2.5">
            <HorizontalRule margin="0" />
            <div className="text-xs text-brand-neutral-50 flex flex-col gap-1.5">
              <div className="flex gap-1">
                <HistoryReservationLocationIcon />
                {item.location}
              </div>
              <div className="flex gap-1">
                <HistoryReservationCalendarIcon />
                {item.startDate && item.endDate
                  ? getDateRangeString(item.startDate, item.endDate)
                  : displayISODate(item.createdAt)}
              </div>
            </div>
            {item.status !== "CANCELED" && (
              <div className="flex flex-col gap-1.5">
                <div className=" bg-brand-primary-magenta bg-clip-text text-transparent font-semibold w-fit text-xs">
                  {item?.statusName}
                </div>
                <ProgressBar
                  currentStep={
                    StepRentStatus[item.status as keyof typeof StepRentStatus]
                  }
                  wrapperClassName="!p-0"
                  className="h-2.5 !bg-brand-primary-magenta"
                  rounded
                  nonSticky
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {index < items.length - 1 && (
        <div className="h-5 w-[24px] flex justify-center">
          <VerticalRule className="!h-full !bg-[#d9d9d9]" />
        </div>
      )}
    </React.Fragment>
  );
}

export const HistoryRentItemIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0.5"
      y="0.5"
      width="39"
      height="39"
      rx="3.5"
      fill="white"
      stroke="#CFCFCF"
    />

    <svg
      x="2"
      y="2"
      width="37"
      height="36"
      viewBox="0 0 37 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.7793 23.8102L13.2055 30.4745V26.7134H21.578V20.9147H13.2055V17.1459L4.7793 23.8102Z"
        stroke="black"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="M32.7792 12.1897L24.353 5.52539V9.29419H15.9805V15.0853H24.353V18.8541L32.7792 12.1897Z"
        fill="url(#paint0_linear_87_16747)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_87_16747"
          x1="15.9805"
          y1="14.6183"
          x2="29.4082"
          y2="12.8442"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#EA27C2" />
          <stop offset="0.992254" stopColor="#E100A3" />
        </linearGradient>
      </defs>
    </svg>
  </svg>
);

export const HistoryRentBookItemIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0.5"
      y="0.5"
      width="39"
      height="39"
      rx="3.5"
      fill="white"
      stroke="#CFCFCF"
    />

    <svg
      x="1.5"
      y="1.5"
      width="37"
      height="37"
      viewBox="0 0 37 37"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.1406 8.64124H5.14062V28.6412H18.1406V8.64124Z"
        stroke="black"
        strokeWidth="1.5"
      />
      <path
        d="M31.1406 8.64124H18.1406V28.6412H31.1406V8.64124Z"
        stroke="black"
        strokeWidth="1.5"
      />
      <path
        d="M12.1667 13.5233H8.77246"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        d="M13.2982 17.2864H8.77246"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M28.7588 9.3894C26.5832 9.3894 25.3635 9.3894 23.188 9.3894V16.2806L25.9827 14.9972L28.7588 16.2806V9.3894Z"
        fill="url(#paint0_linear_2865_42396)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_2865_42396"
          x1="23.188"
          y1="14.0906"
          x2="27.6863"
          y2="13.7094"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#EA27C2" />
          <stop offset="0.992254" stopColor="#E100A3" />
        </linearGradient>
      </defs>
    </svg>
  </svg>
);

export const HistoryRentToolItemIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0.5"
      y="0.5"
      width="39"
      height="39"
      rx="3.5"
      fill="white"
      stroke="#CFCFCF"
    />

    <svg
      x="1.5"
      y="1.5"
      width="37"
      height="37"
      viewBox="0 0 37 37"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.0054 32.3801H30.6693C31.1018 32.3801 31.4525 32.0295 31.4525 31.597L31.4524 22.8095M12.1952 31.8939L30.923 23.161C31.315 22.9782 31.4845 22.5122 31.3017 22.1203L27.5878 14.156C27.405 13.7641 26.939 13.5945 26.5471 13.7773L18.1853 17.6764M15.0075 28.5417C14.2675 31.3034 11.4288 32.9423 8.66717 32.2023C5.9055 31.4623 4.26628 28.6236 5.00627 25.8619L10.3545 5.90219C10.4664 5.48446 10.8956 5.23647 11.3133 5.3484L19.8015 7.62282C20.2193 7.73476 20.4672 8.16418 20.3553 8.58191L15.0075 28.5417Z"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="10.0732"
        cy="26.7451"
        r="2.25342"
        fill="url(#paint0_linear_2865_42386)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_2865_42386"
          x1="7.81982"
          y1="27.5663"
          x2="11.4453"
          y2="27.1862"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#EA27C2" />
          <stop offset="0.992254" stopColor="#E100A3" />
        </linearGradient>
      </defs>
    </svg>
  </svg>
);

export const HistoryRentUmbrellaItemIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0.5"
      y="0.5"
      width="39"
      height="39"
      rx="3.5"
      fill="white"
      stroke="#CFCFCF"
    />
    <path
      d="M24.8501 32.0484C24.8501 33.5043 23.6736 34.6881 22.2104 34.6881C20.7545 34.6881 19.5707 33.5117 19.5707 32.0484V24.1514"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      d="M24.203 11.7915C22.6883 11.1518 21.0192 10.7988 19.2692 10.7988C17.5192 10.7988 15.8501 11.1518 14.3354 11.7915C12.8207 12.4312 11.453 13.365 10.306 14.5121C9.15892 15.6591 8.23245 17.0268 7.59274 18.5415C6.95304 20.0562 6.6001 21.7253 6.6001 23.4753H31.9457C31.9457 21.7253 31.5927 20.0562 30.9457 18.5415"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      d="M19.2692 10.3649V6.68848"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      d="M24.0486 19.7837C25.1856 19.7837 26.1074 18.8619 26.1074 17.7248C26.1074 16.5878 25.1856 15.666 24.0486 15.666C22.9115 15.666 21.9897 16.5878 21.9897 17.7248C21.9897 18.8619 22.9115 19.7837 24.0486 19.7837Z"
      fill="url(#paint0_linear_1394_38597)"
    />
    <path
      d="M31.4015 13.7544C32.5385 13.7544 33.4603 12.8326 33.4603 11.6955C33.4603 10.5585 32.5385 9.63672 31.4015 9.63672C30.2644 9.63672 29.3427 10.5585 29.3427 11.6955C29.3427 12.8326 30.2644 13.7544 31.4015 13.7544Z"
      fill="url(#paint1_linear_1394_38597)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1394_38597"
        x1="26.1074"
        y1="15.666"
        x2="21.7235"
        y2="16.7943"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E100A3" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_1394_38597"
        x1="33.4603"
        y1="9.63672"
        x2="29.0764"
        y2="10.765"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E100A3" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);
